<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { getTextFromClipboard, supportReadFromClipboardAPI } from '@@/bits/clipboard'
import type { ContentPickerSourceMenuListItem, NoSearchFilterError } from '@@/bits/content_picker'
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { getNearestScrollingElement } from '@@/bits/dom'
import { __ } from '@@/bits/intl'
import { SearchFilters } from '@@/bits/web_search'
import OzCloseButton from '@@/library/v4/components/OzCloseButton.vue'
import OzScreenSizeAwareCenterModal from '@@/library/v4/components/OzScreenSizeAwareCenterModal.vue'
import { useContentPickerStore, type ContentPickerSearchMode } from '@@/pinia/content_picker'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AiImageSearchSample from '@@/vuecomponents/AiImageSearchSample.vue'
import ContentPickerSearchBar from '@@/vuecomponents/ContentPickerSearchBar.vue'
import ContentPickerSearchModes from '@@/vuecomponents/ContentPickerSearchModes.vue'
import ContentPickerSearchResults from '@@/vuecomponents/ContentPickerSearchResults.vue'
import ContentPickerSearchSuggestions from '@@/vuecomponents/ContentPickerSearchSuggestions.vue'
import ContentPickerSourceMenuList from '@@/vuecomponents/ContentPickerSourceMenuList.vue'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    isOnline: boolean
    menuList: Array<Array<ContentPickerSourceMenuListItem>>
    preferredTrayStartHeight: number
    searchSuggestions?: string[]
    searchModes?: ContentPickerSearchMode[]
    defaultSearchMode?: SearchFilters
    /**
     * Whether or not the search bar can accept a link (addding a link directly from the search bar).
     * If this is `false`, the search bar will always search the input text.
     */
    canAcceptLinkInSearchBar?: boolean
    minHeight?: number
    xSearchBar?: boolean
    shouldForceFullSize?: boolean
  }>(),
  {
    minHeight: undefined,
    xSearchBar: true,
    shouldForceFullSize: false,
    searchSuggestions: undefined,
    searchModes: undefined,
    defaultSearchMode: undefined,
    canAcceptLinkInSearchBar: true,
  },
)

const emit = defineEmits<{
  (name: 'close'): void
  (name: 'select', source: ContentPickerSource): void
  (
    name: 'select-link',
    url: string,
    payload: { altText?: string; contentSource: ContentPickerSource; thumbnailUrl?: string },
  ): void
}>()

const isSafari = device.safari
const isTouchable = device.touchable

const CLIPBOARD_ITEM_HEIGHT = 60 // clipboard's height is 48px and it's margin is 12px
const DEFAULT_SEARCH_MODES_TABS = {
  [SearchFilters.Images]: { key: SearchFilters.Images, ref: 'imagesButton', text: __('Images') },
  [SearchFilters.Gif]: { key: SearchFilters.Gif, ref: 'gifButton', text: __('GIF') },
  [SearchFilters.Videos]: { key: SearchFilters.Videos, ref: 'youtubeButton', text: __('YouTube') },
  [SearchFilters.Audio]: { key: SearchFilters.Audio, ref: 'spotifyButton', text: __('Spotify') },
  [SearchFilters.Web]: { key: SearchFilters.Web, ref: 'webButton', text: __('Web') },
}

const { isSmallerThanTabletPortrait, windowHeight } = storeToRefs(useWindowSizeStore())
const { isDarkMode } = storeToRefs(useDarkModeStore())

const contentPickerStore = useContentPickerStore()
const {
  searchFilter,
  xSearchScreen,
  isOpenedFromSearch,
  xSearchSuggestions,
  isLinkInputMode,
  isAiImageSearchMode,
  xAiImageResults,
  xAiImagePreview,
  searchQuery,
  currentSearchResultsMap,
  isSearchQueryInProgress,
  isSearchMoreQueryInProgress,
  isError,
  currentSearchResultsForFilter,
  currentSearchMoreUrlForFilter,
  searchSuggestions,
  isImageSearchMode,
  hideBackButton,
} = storeToRefs(contentPickerStore)
const {
  syncSearchSuggestionsWithStorage,
  pickSearchFilter,
  openSearch,
  closeSearch,
  setSearchQuery,
  startLinkInputMode,
  endLinkInputMode,
  openAiImagePreview,
  closeAiImageSearchMode,
  reopenAiImagePreview,
  search,
  clearSearchError,
  searchMore,
} = contentPickerStore

const searchBarRef = ref<InstanceType<typeof ContentPickerSearchBar> | null>(null)
const searchModesRef = ref<InstanceType<typeof ContentPickerSearchModes> | null>(null)

const currentClipboardText = ref<string>('')
const searchFocused = ref<boolean>(false)
const isExpanded = ref<boolean>(false)
const shouldWiggleSearchBar = ref<boolean>(false)

const clipboardEnabled = computed<boolean>(() => {
  return (
    supportReadFromClipboardAPI &&
    !device.safari && // Safari handles clipboard API weirdly so we don't use that at all
    !device.androidApp // Disabling for now until we can find a way to accept clipboard permissions in android webview
  )
})

const xClipboardButton = ref<boolean>(clipboardEnabled.value)

// SEARCH MODES

const currentSearchModes = computed((): ContentPickerSearchMode[] => {
  if (props.searchModes) {
    return props.searchModes
  }

  const buttons: ContentPickerSearchMode[] = []
  const flatMenulist: ContentPickerSourceMenuListItem[] = props.menuList.flatMap((innerMenuList) => innerMenuList)

  for (const item of flatMenulist) {
    if (Object.keys(DEFAULT_SEARCH_MODES_TABS).includes(item.type)) {
      buttons.push(DEFAULT_SEARCH_MODES_TABS[item.type])
    }
  }

  return buttons
})
const defaultSearchMode = computed((): SearchFilters => {
  if (props.defaultSearchMode) {
    return props.defaultSearchMode
  }

  const searchFilters = currentSearchModes.value.map((button) => button.key) as SearchFilters[]
  return searchFilters.includes(SearchFilters.Web) ? SearchFilters.Web : searchFilters[0]
})

// DIALOG

const closeMenu = (): void => {
  emit('close')
}

const handleScrimClick = (): void => {
  // Ignore scrim clicking for iPad
  if (searchFocused.value && isTouchable) return
  closeMenu()
}

const menuListDisabled = ref<boolean>(false)

const handleDragStart = (): void => {
  menuListDisabled.value = true
}

const handleDragEnd = (): void => {
  menuListDisabled.value = false
}

// GREEN BUTTONS

const selectSource = (source: ContentPickerSource): void => {
  trackEvent('Content Picker', `Selected ${source}`)
  endLinkInputMode()
  shouldWiggleSearchBar.value = false
  emit('select', source)
}

const selectSearchResult = (url: string, thumbnailUrl: string): void => {
  trackEvent('Content Picker', 'Selected search result')

  const contentSource = {
    [SearchFilters.Images]: ContentPickerSource.ImageSearch,
    [SearchFilters.Gif]: ContentPickerSource.GifSearch,
    [SearchFilters.Audio]: ContentPickerSource.AudioSearch,
    [SearchFilters.Videos]: ContentPickerSource.VideoSearch,
    [SearchFilters.Web]: ContentPickerSource.WebBrowser,
    [SearchFilters.AiImages]: ContentPickerSource.AiImageSearch,
  }[searchFilter.value ?? defaultSearchMode.value]
  if (isImageSearchMode.value) {
    // only set altText if we are picking images.
    emit('select-link', url, { altText: searchQuery.value, contentSource, thumbnailUrl })
  } else {
    emit('select-link', url, { contentSource, thumbnailUrl })
  }

  emit('close')
}

// CLIPBOARD

const syncClipboardText = async (): Promise<void> => {
  try {
    if (!isSafari && supportReadFromClipboardAPI) {
      currentClipboardText.value = await getTextFromClipboard()
      xClipboardButton.value =
        clipboardEnabled.value && !!currentClipboardText.value && currentClipboardText.value.trim().length > 0
    }
  } catch (e) {
    // do nothing
  }
}

// SEARCHBAR

const searchBarPlaceholder = computed((): string => {
  if (isLinkInputMode.value) {
    return __('Paste or type link here')
  }
  if (isAiImageSearchMode.value) {
    return __('Write what you want to draw')
  }
  if (props.canAcceptLinkInSearchBar) {
    return __('Search web or paste URL')
  }
  return __('Search web')
})

const xBackButton = computed((): boolean => {
  if (hideBackButton.value) {
    return false
  }
  return (
    (xSearchScreen.value && !isOpenedFromSearch.value) || (xAiImagePreview.value && currentSearchModes.value.length > 0)
  )
})

const focusSearchBar = (): void => {
  endLinkInputMode()
  nextTick(() => {
    if (!searchBarRef.value) return
    searchBarRef.value.focusSearchBar()
  })
}

const contentPickerBodyRef = ref<HTMLElement>()

// Clicking on a orange button will go to search menu with the appropriate filter
// Clicking on clipboard will paste text and go to search menu
const imitateSearchBarClickWithFilter = (filter: SearchFilters): void => {
  // Track when image, gif, video, audio, or web search is selected in the content picker
  trackEvent('Content picker', `Selected ${filter}`)
  endLinkInputMode()
  if (!isLinkInputMode.value) {
    openSearch()
    pickSearchFilter({ filter: filter || searchFilter.value || defaultSearchMode.value })
    if (!contentPickerBodyRef.value) return
    const elementInChargeOfScrolling = getNearestScrollingElement(contentPickerBodyRef.value)
    if (!elementInChargeOfScrolling) return
    elementInChargeOfScrolling.scrollTo({ top: 0 })
  }
  shouldWiggleSearchBar.value = false
  focusSearchBar()
}

const pasteText = async (): Promise<void> => {
  try {
    setSearchQuery({ query: (await getTextFromClipboard()).slice(0, 140) })
    imitateSearchBarClickWithFilter(searchFilter.value || defaultSearchMode.value)
  } catch (e) {
    // Reference: https://webkit.org/blog/10247/new-webkit-features-in-safari-13-1/, search for "Async Clipboard API"
    // Safari requires navigator.clipboard calls to be called within user gesture event handlers instead of a permissions-based model for reading from the clipboard
    // When getTextFromClipboard is called programatically like this, Safari will show a small "Paste" button on the UI to confirm if the user allows pasting
    // If they tap/click outside the button, Safari treat it as permission denied and throws a NotAllowedError => we can ignore it
    if (isSafari && e.name === 'NotAllowedError') {
      return
    }
    // throw unknown errors again so they will go to Sentry
    throw e
  }
}

const handleSearchBarSearch = (query): void => {
  if (isLinkInputMode.value) {
    pickSearchFilter({ filter: SearchFilters.Web })
  } else if (isAiImageSearchMode.value) {
    pickSearchFilter({ filter: SearchFilters.AiImages })
  }
  search({ query })
}

// When user click on the searchbar, we open search menu and pre-pick the previous filter
// or "None" filter to not pre-pick any.
const handleSearchBarClick = (): void => {
  if (!isLinkInputMode.value && !isAiImageSearchMode.value) {
    openSearch()
    if (!searchFilter.value) {
      pickSearchFilter({ filter: searchFilter.value || defaultSearchMode.value })
    }
  }
  focusSearchBar()
}

const handleSearchBarFocus = (): void => {
  syncClipboardText()
  searchFocused.value = true
}

const handleSearchBarBlur = (): void => {
  searchFocused.value = false
}

const triggerLinkInput = (): void => {
  startLinkInputMode()
  focusSearchBar()
  // wiggle the search bar again every time link input is triggered
  shouldWiggleSearchBar.value = false
  nextTick(() => setTimeout(() => (shouldWiggleSearchBar.value = true)))
}

const triggerAiImageSearch = (): void => {
  trackEvent('Content picker', 'Selected AI image search')
  openAiImagePreview()
  focusSearchBar()
}

const goBack = (): void => {
  closeSearch()
  closeAiImageSearchMode()
  setSearchQuery({ query: '' })
  isExpanded.value = false
}

const handleSearchBarError = (error: NoSearchFilterError): void => {
  if (error && error === 'NO_SEARCH_FILTER') {
    searchModesRef.value?.shake()
  }
}
// SEARCH QUERYING

const clearSearch = (): void => {
  if (isAiImageSearchMode.value) {
    reopenAiImagePreview()
  }
  setSearchQuery({ query: '' })
}

const filterSelected = (filter: string): void => {
  trackEvent('Content Picker', `Selected ${filter}`)
  openSearch()
  pickSearchFilter({ filter: filter as SearchFilters })
  if (!searchQuery.value || searchQuery.value.trim() === '') {
    focusSearchBar()
    return
  }

  if (!searchFilter.value) {
    return
  }

  // Search immediately after clicking the filter
  // if the results are not populated yet.
  if (
    !currentSearchResultsMap.value ||
    !currentSearchResultsMap.value[searchFilter.value] ||
    currentSearchResultsMap.value[searchFilter.value].length === 0
  ) {
    search()
  }
}

const suggestionSelected = (suggestion: string): void => {
  setSearchQuery({ query: suggestion })
  search()
}

// SEARCH RESULTS

onMounted((): void => {
  syncSearchSuggestionsWithStorage()
  syncClipboardText()
  document.addEventListener('visibilitychange', () => {
    syncClipboardText()
  })
})

watch(
  xSearchScreen,
  (newVal: boolean) => {
    if (newVal && !isAiImageSearchMode.value) {
      if (isOpenedFromSearch.value) {
        setTimeout(() => {
          // Because we have an input field that needs focus, we delay the animation
          // until after the keyboard is popped up
          isExpanded.value = true
        }, 250)
      } else {
        isExpanded.value = true
      }
    } else {
      isExpanded.value = false
    }
  },
  { immediate: true },
)

/**
 *  If search query is being typed/change, likely when clicking paste button
 *  we make sure search menu is always shown
 */
watch(searchQuery, (val) => {
  if (!xSearchScreen.value && val && val.trim() !== '') {
    imitateSearchBarClickWithFilter(searchFilter.value || defaultSearchMode.value)
  }
})
</script>

<template>
  <OzScreenSizeAwareCenterModal
    :dir="dir()"
    :is-phone="isSmallerThanTabletPortrait"
    :window-height="windowHeight"
    :class="['select-none', 'deep-font-sans', isDarkMode && 'dark-mode']"
    :width-class="isAiImageSearchMode ? 'w-135' : 'w-180'"
    :shadow-class="'shadow-elevation-4'"
    :disable-drag="xSearchScreen"
    :full-size="isExpanded || shouldForceFullSize"
    :preferred-tray-start-height="
      xClipboardButton ? preferredTrayStartHeight + CLIPBOARD_ITEM_HEIGHT : preferredTrayStartHeight
    "
    :min-height="minHeight"
    :x-scrim="true"
    data-testid="contentPickerSourceMenu"
    @scrim-click="handleScrimClick"
    @scrim-esc="closeMenu"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <template #header>
      <ContentPickerSearchBar
        v-if="xSearchBar || isAiImageSearchMode"
        ref="searchBarRef"
        :x-back-button="xBackButton"
        :x-clipboard-button="xClipboardButton"
        :should-wiggle="shouldWiggleSearchBar"
        :should-force-focus-on-mount="isLinkInputMode"
        :submit-button-data-id="isAiImageSearchMode ? 'ai-image-search-trigger' : ''"
        :search-bar-placeholder="searchBarPlaceholder"
        :search-query="searchQuery"
        :can-accept-link="canAcceptLinkInSearchBar"
        @search="handleSearchBarSearch"
        @focus="handleSearchBarFocus"
        @blur="handleSearchBarBlur"
        @copy="syncClipboardText"
        @click="handleSearchBarClick"
        @clear-search="clearSearch"
        @select="selectSearchResult"
        @back="goBack"
        @paste-click="pasteText"
        @close="closeMenu"
        @error="handleSearchBarError"
        @input="(query) => setSearchQuery({ query })"
      />
      <div v-else class="p-4">
        <OzCloseButton @click="closeMenu" />
      </div>
    </template>
    <template #body>
      <div ref="contentPickerBodyRef" :class="['flex flex-col grow', !xAiImageResults && 'overflow-auto']">
        <!-- AI image search -->
        <AiImageSearchSample
          v-if="xAiImagePreview"
          @run-sample-search="setSearchQuery({ query: $event.searchQuery })"
        />
        <ContentPickerSearchResults
          v-else-if="xAiImageResults && searchFilter"
          :is-online="isOnline"
          :search-query="searchQuery"
          :search-filter="searchFilter"
          :results="currentSearchResultsForFilter"
          :is-search-query-in-progress="isSearchQueryInProgress"
          :is-search-more-query-in-progress="isSearchMoreQueryInProgress"
          :is-ai-image-search-mode="isAiImageSearchMode"
          :can-search-more="!!currentSearchMoreUrlForFilter"
          :has-error="isError"
          @select="selectSearchResult"
          @search-more="searchMore"
          @clear-search-error="clearSearchError"
        />

        <!-- Master list of all content sources -->
        <ContentPickerSourceMenuList
          v-else-if="!xSearchScreen"
          :menu-list="menuList"
          :x-clipboard-button="xClipboardButton && xSearchBar"
          :clipboard-text="currentClipboardText"
          :is-disabled-on-drag="menuListDisabled"
          :is-phone-screen="isSmallerThanTabletPortrait"
          class="stroller"
          @close="closeMenu"
          @select="selectSource"
          @select-link="$emit('select-link', $event, { contentSource: ContentPickerSource.LinkInput })"
          @focus-search-bar="imitateSearchBarClickWithFilter"
          @paste-text="pasteText"
          @trigger-link-input="triggerLinkInput"
          @trigger-ai-image-search="triggerAiImageSearch"
        />
        <!-- Built-in search -->
        <template v-else>
          <ContentPickerSearchModes
            v-if="currentSearchModes.length > 1 && searchFilter"
            ref="searchModesRef"
            :search-modes="currentSearchModes"
            :current-search-filter="searchFilter"
            @filter-selected="filterSelected"
          />
          <ContentPickerSearchSuggestions
            v-if="xSearchSuggestions"
            :class="['pt-4']"
            :search-suggestions="props.searchSuggestions || searchSuggestions"
            @suggestion-selected="suggestionSelected"
          />
          <div v-else :class="['pt-2', 'overflow-auto scrollbar-regular dark:scrollbar-regular-dark']">
            <ContentPickerSearchResults
              :is-online="isOnline"
              :search-query="searchQuery"
              :search-filter="searchFilter"
              :results="currentSearchResultsForFilter"
              :is-search-query-in-progress="isSearchQueryInProgress"
              :is-search-more-query-in-progress="isSearchMoreQueryInProgress"
              :is-ai-image-search-mode="isAiImageSearchMode"
              :can-search-more="!!currentSearchMoreUrlForFilter"
              :has-error="isError"
              @select="selectSearchResult"
              @search-more="searchMore"
              @clear-search-error="clearSearchError"
            />
          </div>
        </template>
      </div>
    </template>
  </OzScreenSizeAwareCenterModal>
</template>

<style lang="postcss">
.deep-font-sans {
  * {
    @apply font-sans;
    @apply break-words;
  }
}
</style>
